<template>
  <div class="auth-page">
    <div class="auth-shell" v-if="!user">
      <section class="auth-card">
        <div class="auth-card-header">
          <div class="auth-signin-heading">
            <span class="auth-signin-icon"><img :src="brand.assets.logoMark" :alt="brand.productName" /></span>
            <h1>Sign in</h1>
          </div>
          <p>Enter your username and password to access your account.</p>
        </div>

        <div class="auth-card-content">
          <transition name="auth-notice">
            <div class="auth-alert is-error" role="alert" v-if="error">{{ error }}</div>
          </transition>
          <transition name="auth-notice">
            <div class="auth-alert is-success" role="status" v-if="success">{{ success }}</div>
          </transition>

          <form @submit.prevent="handleNext" class="auth-form">
            <label class="auth-field">
              <span>Username</span>
              <input class="browser-default" type="text" v-model="username" :disabled="isSubmitting" required placeholder="Enter your username" @input="handleFocus" autocomplete="username" autocapitalize="none" spellcheck="false" />
            </label>

            <transition name="auth-notice">
              <div class="auth-password-section" v-if="isChecked">
                <label class="auth-field">
                  <span>Password</span>
                  <span class="auth-password-control">
                    <input ref="passwordInput" class="browser-default" :type="showPassword ? 'text' : 'password'" v-model="password" :disabled="isSubmitting" required
                      placeholder="Enter your password" autocomplete="current-password" />
                    <button type="button" :title="showPassword ? 'Hide password' : 'Show password'"
                      :aria-label="showPassword ? 'Hide password' : 'Show password'" @click="showPassword = !showPassword">
                      <EyeOff v-if="showPassword" :size="17" />
                      <Eye v-else :size="17" />
                    </button>
                  </span>
                </label>
                <button type="button" class="auth-forgot-link" @click="handleForgot">Forgot password?</button>
              </div>
            </transition>

            <button type="submit" class="auth-submit" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="auth-spinner" aria-hidden="true"></span>
              <LogIn v-else :size="17" />
              <span>{{ isSubmitting ? (isChecked ? "Signing in..." : "Checking...") : (isChecked ? "Sign in" : "Continue") }}</span>
            </button>

            <transition name="auth-notice">
              <div class="auth-reset-notice" v-if="forgotNotice">
                <span>{{ forgotNotice }}</span>
                <button type="button" @click="clearForgotNotice" aria-label="Dismiss password reset note"><X :size="15" /></button>
              </div>
            </transition>
          </form>
        </div>

        <footer class="auth-card-footer">
          Access is limited to approved {{ brand.workspaceLabel.toLowerCase() }} accounts.
        </footer>
      </section>
    </div>

    <div class="login-update-shell" v-else>
      <UpdateUserDiv :user="user" :temporary-password="password" @updated="handleUpdate" />
    </div>
  </div>
</template>

<script>
import { nextTick, onMounted, ref, watch } from "vue";
import { Eye, EyeOff, LogIn, X } from "@lucide/vue";
import postItem from "@/composables/postItem";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { addModules } from "@/composables/cache/modules";
import {
  consumeAuthExpiredNotice,
  resetAuthRequestGuard,
} from "@/composables/authRequestGuard";
import UpdateUserDiv from "@/components/login/UpdateUserComponent.vue";
import { ABBR } from "@/laravel/config";
import brand from "@/whitelabel/brand.config";

export default {
  name: "LoginHome",
  components: { Eye, EyeOff, LogIn, UpdateUserDiv, X },
  setup() {
    const isSubmitting = ref(false);
    const user = ref(null);
    const username = ref(null);
    const password = ref(null);
    const passwordInput = ref(null);
    const showPassword = ref(false);
    const isChecked = ref(false);
    const unregUser = ref(null);
    const error = ref(null);
    const success = ref(null);
    const forgotNotice = ref(null);
    const adminLabel = `${ABBR || "HMO"} Admin`;

    // vue router
    const router = useRouter();

    // store
    const store = useStore();
    watch(isChecked, async (checked) => {
      if (checked) {
        await nextTick();
        passwordInput.value?.focus();
      }
    });

    // start
    onMounted(() => {
      // page name
      document.title = "Login";

      const expiredNotice = consumeAuthExpiredNotice();
      if (expiredNotice) {
        error.value = expiredNotice;
      }
    });

    // reset login
    onMounted(() => {
      if (!store.state.user) return;
      router.replace({ name: "home" });
    });

    // get in
    const getIn = (userVal) => {

      // load login history
      store.commit("loadLoginHistory", userVal?.email);

      // load user
      resetAuthRequestGuard();
      store.commit("loadUser", userVal);
      store.commit("loadDashboardFreshSignin", true);

      // refer loading
      router.push({
        name: "loading",
      });

    }

    // clear
    const clearMessages = () => {
      error.value = null;
      success.value = null;
      forgotNotice.value = null;
    }

    const getLoginRequestMessage = (type, stage = "signin") => {
      if (type === "validation") {
        return stage === "check" ? "Please enter your username." : "Please enter your username and password.";
      }

      if (type === "network") {
        return `Unable to reach ${brand.productName}. Please check your connection and try again.`;
      }

      return "Sign in is temporarily unavailable. Please try again shortly.";
    }

    // handle next
    const handleNext = () => {
      if (isSubmitting.value) return;
      username.value = String(username.value || "").trim();
      if (!username.value || (isChecked.value && !password.value)) {
        error.value = getLoginRequestMessage("validation", isChecked.value ? "signin" : "check");
        return;
      }
    // clear messages
    clearMessages();
    // start
      if (!isChecked.value) {
        // check loginHistory
        if (store.state?.loginHistory === username.value) {
          // user exists
          isChecked.value = true;
        } else {
          // check user
          handleUserCheck();
        }
      } else {
        // sign in
        handleSignIn();
      }
    };

    // signCheck
    const handleUserCheck = () => {
      // start
      isSubmitting.value = true;

      (async () => {
        const { res, errorType } = await postItem("authority/signin/user/check", {
          username: username.value,
        });
        if (res.value) {
          if (res.value?.success) {
            // user exists
            isChecked.value = true;
          } else {
            // user doesn't exist
            // start
            let newVal = null;
            // prepare for account creation
            if (res.value?.client) {
              // client
              let clientModules = addModules()
                .filter(x => x.type === "client")
                .map(item => item.to);

              let clientSchemes = res.value.schemes.filter(x => x.type === "p")
                .map(item => item.id);

              newVal = {
                email: res.value?.client?.email,
                tag: res.value?.client?.id,
                schemes: clientSchemes,
                modules: clientModules,
                type: "client",
              }

            } else if (res.value?.provider) {
              // provider
              let providerModules = addModules()
                .filter(x => x.type === "provider")
                .map(item => item.to);

              let providerSchemes = res.value.schemes
                .map(item => item.id);

              newVal = {
                email: res.value?.provider?.email,
                tag: res.value?.provider?.id,
                schemes: providerSchemes,
                modules: providerModules,
                type: "provider",
              }
            } else if (res.value?.principal) {
              // principal
              let principalModules = addModules()
                .filter(x => x.type === "principal")
                .map(item => item.to);

              let principalSchemes = res.value.schemes.filter(x => x.type === "p")
                .map(item => item.id);

              newVal = {
                email: res.value?.principal?.email,
                tag: res.value?.principal?.id,
                schemes: principalSchemes,
                modules: principalModules,
                type: "principal",
              }
            } else {
              error.value = `We could not find an account for that username. Please contact ${adminLabel}.`;
            }

            // set unreg user
            if (newVal) {
              const { res: resCreate } = await postItem("authority/signin/user/create", {
                ...newVal,
              });
              if (resCreate.value) {
                isChecked.value = true;
                success.value = `Check your email (${newVal.email}) for your new login password.`;
                unregUser.value = resCreate.value.data;
              } else {
                error.value = `We could not prepare this account right now. Please contact ${adminLabel}.`;
              }
            }
          }
        } else {
          error.value = getLoginRequestMessage(errorType.value, "check");
        }
        // end end
        isSubmitting.value = false;
      })();

    };

    // handle 
    const handleFocus = () => {
      if (isSubmitting.value) return;
      // clear messages
      clearMessages();
      // reset
      isChecked.value = false;
      password.value = null;
      showPassword.value = false;
    }

    // signIn
    const handleSignIn = () => {
      // start
      isSubmitting.value = true;
      error.value = null;

      (async () => {
        const { res, errorType } = await postItem("authority/signin", {
          username: username.value,
          password: password.value,
        });
        if (res.value) {
          if (res.value?.success) {
            // A verified sign-in starts a fresh authenticated session, including
            // the pending-user account setup flow.
            resetAuthRequestGuard();

            if (res.value.data.status === 'a') {

              // moveee
              getIn(res.value.data);

            } else if (res.value.data.status === 'p') {

              // load user for update
              user.value = res.value.data;

            } else {
              error.value = `This account cannot be used right now. Please contact ${adminLabel}.`;
            }

          } else if (res.value?.reason === "account_unavailable") {
            error.value = `This account cannot be used right now. Please contact ${adminLabel}.`;
          } else {
            error.value = "We could not sign you in with those details. Please check and try again.";
          }

        } else {
          error.value = getLoginRequestMessage(errorType.value, "signin");
        }
        // end end
        isSubmitting.value = false;
      })();

    };

    // send updated folk
    const handleUpdate = (userVal) => {
      getIn(userVal);
    }

    // handle forgot
    const handleForgot = () => {
      // clear messages
      clearMessages();
      forgotNotice.value = `Contact ${adminLabel} for password reset.`;
    }

    const clearForgotNotice = () => {
      forgotNotice.value = null;
    }

    return {
      brand,
      handleNext,
      handleSignIn,
      handleUpdate,
      handleForgot,
      handleFocus,
      clearForgotNotice,
      isSubmitting,
      user,
      username,
      password,
      passwordInput,
      isChecked,
      unregUser,
      error,
      success,
      forgotNotice,
      showPassword,
    };
  },
};
</script>

<style scoped>
.auth-page {
  --login-accent: #0f766e;
  --login-accent-hover: #115e59;
  box-sizing: border-box;
  align-items: center;
  background: color-mix(in srgb, var(--login-accent) 5%, var(--ui-background));
  color: var(--ui-foreground);
  display: flex;
  justify-content: center;
  min-height: 100vh;
  min-height: 100svh;
  padding: 2rem 1rem;
}
.auth-shell { display: grid; gap: 1rem; max-width: 24rem; width: 100%; }
.auth-brand { align-items: center; display: flex; gap: .65rem; justify-content: center; margin-bottom: .25rem; }
.auth-brand-full { background: #ffffff; border-radius: var(--ui-radius); box-sizing: border-box; height: auto; max-width: 100%; object-fit: contain; padding: .5rem .75rem; width: 15rem; }
.auth-brand-mark { align-items: center; background: var(--login-accent); border-radius: var(--ui-radius); display: flex; height: 2.25rem; justify-content: center; width: 2.25rem; }
.auth-brand-mark img { height: 1.55rem; object-fit: contain; width: 1.55rem; }
.auth-brand strong { font-family: var(--ui-font-display); font-size: 1.1rem; font-weight: 600; }

.auth-card {
  background: var(--ui-card);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  box-shadow: var(--ui-shadow-xs);
  color: var(--ui-card-foreground);
  overflow: hidden;
}
.auth-card-header { background: color-mix(in srgb, var(--login-accent) 4%, var(--ui-card)); padding: 1.5rem 1.5rem .75rem; }
.auth-signin-heading { align-items: center; display: flex; gap: .65rem; justify-content: flex-start; }
.auth-signin-icon { align-items: center; background: #0f172a; border-radius: calc(var(--ui-radius) - 2px); display: flex; flex: 0 0 2.25rem; height: 2.25rem; justify-content: center; }
.auth-signin-icon img { display: block; height: 2rem; object-fit: contain; width: 2rem; }
.auth-card-header h1 { font-family: var(--ui-font-display); font-size: 1.15rem; font-weight: 700; letter-spacing: -.02em; margin: 0; }
.auth-card-header p { color: var(--ui-muted-foreground); font-size: .85rem; line-height: 1.5; margin: .4rem 0 0; }
.auth-card-content { display: grid; gap: .75rem; padding: .75rem 1.5rem 1.5rem; }
.auth-form { display: grid; gap: .9rem; }
.auth-field { display: grid; gap: .4rem; }
.auth-field > span:first-child { color: var(--ui-foreground); font-size: .78rem; font-weight: 600; }
.auth-field input {
  box-sizing: border-box;
  background: var(--ui-background);
  border: 1px solid var(--ui-input);
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none;
  color: var(--ui-foreground);
  font: inherit;
  font-size: .88rem;
  height: 2.65rem;
  margin: 0;
  padding: 0 .75rem;
  width: 100%;
}
.auth-field input:focus { border-color: var(--login-accent); box-shadow: 0 0 0 2px color-mix(in srgb, var(--login-accent) 20%, transparent); outline: 0; }
.auth-password-section { display: grid; gap: .35rem; }
.auth-password-control { display: block; position: relative; }
.auth-password-control input { padding-right: 2.75rem; }
.auth-password-control button {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--ui-muted-foreground);
  display: flex;
  height: 2.5rem;
  justify-content: center;
  position: absolute;
  right: .15rem;
  top: .08rem;
  width: 2.5rem;
  cursor: pointer;
}
.auth-forgot-link { background: transparent; border: 0; color: var(--ui-muted-foreground); font-size: .75rem; font-weight: 600; justify-self: end; padding: 0; }
.auth-forgot-link:hover { color: var(--login-accent); }
.auth-submit {
  cursor: pointer;
  align-items: center;
  background: var(--login-accent);
  border: 1px solid var(--login-accent);
  border-radius: calc(var(--ui-radius) - 2px);
  color: #ffffff;
  display: flex;
  font: inherit;
  font-size: .84rem;
  font-weight: 600;
  gap: .5rem;
  height: 2.65rem;
  justify-content: center;
  margin-top: .2rem;
  width: 100%;
}
.auth-submit:hover:not(:disabled) { background: var(--login-accent-hover); border-color: var(--login-accent-hover); }
.auth-submit:focus-visible { outline: 2px solid var(--login-accent); outline-offset: 3px; }
.auth-submit:disabled { cursor: wait; opacity: .65; }
.auth-spinner { animation: auth-spin .7s linear infinite; border: 2px solid color-mix(in srgb, currentColor 35%, transparent); border-radius: 50%; border-top-color: currentColor; height: 1rem; width: 1rem; }
.auth-alert, .auth-reset-notice { border: 1px solid var(--ui-border); border-radius: calc(var(--ui-radius) - 2px); font-size: .78rem; line-height: 1.45; padding: .65rem .75rem; }
.auth-alert.is-error { background: color-mix(in srgb, var(--ui-destructive) 8%, var(--ui-background)); border-color: color-mix(in srgb, var(--ui-destructive) 25%, var(--ui-border)); color: var(--ui-destructive); }
.auth-alert.is-success { background: color-mix(in srgb, var(--ui-success) 8%, var(--ui-background)); border-color: color-mix(in srgb, var(--ui-success) 25%, var(--ui-border)); color: var(--ui-success); }
.auth-reset-notice { align-items: center; background: var(--ui-muted); color: var(--ui-muted-foreground); display: flex; gap: .5rem; justify-content: space-between; }
.auth-reset-notice button { align-items: center; background: transparent; border: 0; color: inherit; display: flex; padding: .2rem; }
.auth-card-footer { background: color-mix(in srgb, var(--login-accent) 3%, var(--ui-card)); border-top: 1px solid var(--ui-border); color: var(--ui-muted-foreground); font-size: .7rem; line-height: 1.45; padding: 1rem 1.5rem; text-align: center; }
.login-update-shell { max-width: 56rem; width: 100%; }
.auth-notice-enter-active, .auth-notice-leave-active { transition: opacity 150ms ease, transform 150ms ease; }
.auth-notice-enter-from, .auth-notice-leave-to { opacity: 0; transform: translateY(-.25rem); }
@keyframes auth-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .auth-notice-enter-active, .auth-notice-leave-active { transition: none; }
}
@media (max-width: 480px) {
  .auth-page { align-items: flex-start; padding-top: 2.5rem; }
  .auth-card-header, .auth-card-content { padding-left: 1.15rem; padding-right: 1.15rem; }
}
</style>
